/**
 * <Can> — render the default slot only when the signed-in staff member meets
 * the requirement, otherwise the optional #fallback slot. Reactive.
 *
 *   <Can perm="tasks.create"><button>New task</button></Can>
 *   <Can perm="payroll.view" scope="all">…<template #fallback>…</template></Can>
 *   <Can :any="['leads.view', 'clients.view']">…</Can>
 *   <Can :all="['invoices.create', 'payments.create']">…</Can>
 *
 * No requirement → nothing is rendered (fail closed). A render-function
 * component (not an SFC) so it is unit-testable without the Vue plugin.
 * File name: CanGate.ts, because Can.ts would collide with can.ts on
 * case-insensitive file systems (macOS).
 */
import { computed, defineComponent, type PropType } from 'vue';
import { useAuthStore } from '../stores/auth';
import type { PermissionRequirement, PermissionSlug, Scope } from './types';

interface CanProps {
  perm?: PermissionRequirement;
  any?: readonly PermissionSlug[];
  all?: readonly PermissionSlug[];
  scope?: Scope;
}

export function requirementFromProps(props: CanProps): PermissionRequirement | null {
  if (props.perm) {
    if (typeof props.perm === 'string') return props.scope ? { slug: props.perm, scope: props.scope } : props.perm;
    return props.perm;
  }
  if (props.any?.length) return { anyOf: props.any, scope: props.scope };
  if (props.all?.length) return { allOf: props.all, scope: props.scope };
  return null;
}

export default defineComponent({
  name: 'Can',
  props: {
    perm: { type: [String, Object] as PropType<PermissionRequirement>, default: undefined },
    any: { type: Array as PropType<readonly PermissionSlug[]>, default: undefined },
    all: { type: Array as PropType<readonly PermissionSlug[]>, default: undefined },
    scope: { type: String as PropType<Scope>, default: undefined },
  },
  setup(props, { slots }) {
    const auth = useAuthStore();
    const allowed = computed(() => auth.meets(requirementFromProps(props)));
    return () => (allowed.value ? slots.default?.() : slots.fallback?.()) ?? null;
  },
});
